// src/components/sections/section-latest-stories.tsx
import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { DATALAYER_TYPE, sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';
import WidgetHeader from 'src/components/widgets/widget-header';
import CardMainNews from 'src/components/cards/card-main-news';
import CardMostView from 'src/components/cards/card-most-view';
import CardCarouselNews from 'src/components/cards/card-carousel-news';
import Carousels from 'src/components/carousels';
import useResponsive from 'src/utils/device';

type Props = {
  latestNews: any[];
  mostView: any[];
  title?: string;
  mostViewTitle?: string;
};

const SectionLatestStories = ({ latestNews = [], mostView = [], title = 'LATEST STORIES', mostViewTitle = 'MOST VIEW' }: Props) => {
  const router = useRouter();

  // แบ่ง data
  const mainNews = latestNews[0];
  const subNews = latestNews.slice(1, 3);
  const mostViewNews = mostView.slice(0, 3);
  const carouselNews = latestNews.slice(0, 3);
  const responsive = useResponsive();

  if (isEmpty(latestNews)) return null;

  return (
    <SectionWrapper>
      {/* Header */}
      <WidgetHeader viewMoreLink={'/category/latest-news'} as='h2' title={title} titleColor='#ffffff' variant='line' position='left' showViewMore={false} />
      <div className='container'>
        <div className='content-wrapper'>
          {/* ========== Left Side ========== */}
          <div className='left-side'>
            {!responsive.isMobile ? (
              <>
                {/* Main News */}
                {mainNews && (
                  <CardMainNews
                    data={mainNews}
                    showTitle={true}
                    showCategory={true}
                    type='CARD_MEDIUM'
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'section-3',
                      position: 'ใต้ : section-2',
                      data: {
                        title: mainNews?.title,
                        heading: title,
                        index: 1
                      }
                    }}
                  />
                )}
                {/* Sub News Grid */}
                <div className='sub-news-grid'>
                  {subNews.map((item: any, index: number) => (
                    <div key={index}>
                      <CardMainNews
                        data={item}
                        showTitle={false}
                        showCategory={false}
                        type='CARD_SMALL'
                        eventDataLayer={{
                          type: DATALAYER_TYPE.TRACK_POSITION,
                          router: router?.pathname,
                          section: 'section-3',
                          position: 'ใต้ : section-2',
                          data: {
                            title: item?.title,
                            heading: title,
                            index: index + 2
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className='carousel-wrapper'>
                <Carousels mobileSlideShowTwo={1} pauseOnHover={false} autoplaySpeed={6000} arrows={carouselNews && true}>
                  {carouselNews.map((item: any, index: number) => (
                    <div key={item?.id || index} className='card-items'>
                      <CardCarouselNews
                        data={item}
                        showTitle={true}
                        showCategory={true}
                        showBlurb={false}
                        type='CARD_SMALL'
                        eventDataLayer={{
                          type: DATALAYER_TYPE.TRACK_POSITION,
                          router: router?.pathname,
                          section: 'section-3',
                          position: 'ใต้ : section-2',
                          data: {
                            title: item?.title,
                            heading: title,
                            carousel: index + 1
                          }
                        }}
                      />
                    </div>
                  ))}
                </Carousels>
              </div>
            )}
          </div>

          {/* ========== Right Side ========== */}
          <div className='right-side'>
            <h3 className='most-view-title'>{mostViewTitle}</h3>

            <div className='most-view-list'>
              {mostViewNews.map((item: any, index: number) => (
                <CardMostView
                  key={item?.id || index}
                  data={item}
                  showTitle={true}
                  showCategory={true}
                  type='CARD_SMALL'
                  eventDataLayer={{
                    type: DATALAYER_TYPE.TRACK_POSITION,
                    router: router?.pathname,
                    section: 'section-3',
                    position: 'ใต้ : section-2',
                    data: {
                      title: item?.title,
                      heading: 'most-view',
                      index: index + 1
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

const SectionWrapper = styled.section`
  background-color: #000000;
  padding: 60px 0 30px 0;
  /* padding: 40px 20px; */

  .variant-line.position-left .title-link {
    background-color: #000000;
    padding: 0 20px 0;
    .title {
      font-size: 45px;
    }
    @media (max-width: 768px) {
      padding: 0px;
      .title {
        font-size: 25px;
        font-weight: 400;
        letter-spacing: 2px;
      }
    }
  }
  .variant-line .full-width-line {
    background-color: #ffffff;
    top: 40%;
    @media (max-width: 768px) {
      background-color: unset;
    }
  }
  .variant-line {
    padding: 10px 0 30px;
    @media (max-width: 768px) {
      padding: 5px 0 15px;
    }
  }
  .section-title {
    color: #fff;
    font-size: 32px;
    font-weight: 700;
    font-style: italic;
    margin: 0 0 30px;
    text-transform: uppercase;
    letter-spacing: 2px;

    @media (max-width: 768px) {
      font-size: 24px;
      margin-bottom: 20px;
    }
  }

  .content-wrapper {
    display: grid;
    grid-template-columns: 1.3fr 1fr;
    gap: 20px;
    overflow: hidden;
    min-width: 0;
    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
      gap: 0px;
    }
  }

  /* ========== Left Side ========== */
  .left-side {
    display: flex;
    flex-direction: column;
    gap: 15px;
    justify-content: space-between;
    @media (max-width: 768px) {
      min-width: 0;
      overflow: hidden;
      .slick-prev,
      .slick-next {
        top: 33%;
      }
    }
  }

  .main-news {
    position: relative;
    display: block;
    text-decoration: none;
    border-radius: 5px;
    overflow: hidden;

    &:hover {
      .main-thumb img {
        transform: scale(1.05);
      }
      .main-title {
        color: #ed1a3b;
      }
    }
  }

  .main-thumb {
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;

    .resolution-image,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }

    .category-tag {
      position: absolute;
      top: 15px;
      right: 15px;
      background-color: rgba(0, 0, 0, 0.7);
      color: #fff;
      padding: 5px 12px;
      font-size: 12px;
      border-radius: 4px;
    }
  }

  .main-content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));

    .category {
      display: inline-block;
      color: #ffffff;
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
      text-transform: uppercase;
      border-left: 2px solid #ed1a3b;
      padding-left: 5px;
      line-height: 1.5;
    }

    .main-title {
      color: #fff;
      font-size: 20px;
      font-weight: 500;
      line-height: 1.4;
      margin: 0;
      transition: color 0.3s ease;

      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;

      @media (max-width: 768px) {
        font-size: 16px;
      }
    }
  }

  /* Sub News Grid */
  .sub-news-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;

    @media (max-width: 600px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
    }
  }

  .sub-news-item {
    display: block;
    border-radius: 5px;
    overflow: hidden;

    &:hover {
      .sub-thumb img {
        transform: scale(1.05);
      }
    }
  }

  .sub-thumb {
    aspect-ratio: 16 / 9;
    overflow: hidden;

    .resolution-image,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
  }

  /* ========== Right Side ========== */
  .right-side {
    display: flex;
    flex-direction: column;
  }

  .most-view-title {
    color: #fff;
    font-size: 38px;
    font-weight: 700;
    margin: 35px 0 35px 0;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-left: 5px solid #ed1a3b;
    padding-left: 15px;
    line-height: 1.05;

    @media (max-width: 768px) {
      font-size: 22px;
      margin-top: 0px;
    }
  }

  .most-view-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .most-view-item {
    display: flex;
    gap: 15px;
    text-decoration: none;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateX(5px);

      .most-view-item-title {
        color: #ed1a3b;
      }

      .most-view-thumb img {
        transform: scale(1.05);
      }
    }
  }

  .most-view-thumb {
    flex-shrink: 0;
    width: 50%;
    aspect-ratio: 16 / 9;
    border-radius: 6px;
    overflow: hidden;
    min-width: 126px;

    .resolution-image,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }

    @media (max-width: 768px) {
      width: 120px;
    }
  }

  .most-view-content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 8px;
  }

  .most-view-item-title {
    color: #fff;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.4;
    margin: 0;
    transition: color 0.3s ease;

    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media (max-width: 768px) {
      font-size: 14px;
      -webkit-line-clamp: 2;
    }
  }

  .most-view-category {
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    border-left: 2px solid #ed1a3b;
    padding-left: 5px;
    line-height: 1.5;
  }

  .carousel-wrapper {
    width: 100%;
    max-width: 100%;
    overflow: hidden;

    .carousel {
      .slick-slider {
        .slick-list {
          margin: 0 -5px;
        }
      }
    }
    .slick-slide {
      > div {
        padding: 0 5px;
      }
    }
  }

  .card-items {
    width: 100%;
    @media (max-width: 768px) {
      padding-left: 0px;
      padding-right: 0px;
    }
  }
  @media (max-width: 768px) {
    padding: 30px 0 30px 0;
  }
`;

export default SectionLatestStories;
